import http from "../utils/http";

/**
 * Time-series body composition scans for a member.
 * Backend: `/admin/body-measurement` — every response is wrapped in the
 * standard BizResponse envelope, so payloads are unwrapped from `.data.data`.
 */
export interface BodyMeasurement {
  id: string;
  userId: string;
  measuredAt: string;
  measuredBy?: string;
  weightKg: number;
  skeletalMuscleKg: number;
  bodyFatKg: number;
  heightCm?: number;
  bodyFatPct?: number;
  skeletalMusclePct?: number;
  visceralFatLevel?: number;
  bodyAge?: number;
  bmi?: number;
  restingMetabolismKcal?: number;
  note?: string;
  createdAt?: string;
  updatedAt?: string;
}

/** Fields the admin sends when creating/upserting a scan (`measuredBy` is server-set). */
export interface CreateBodyMeasurementPayload {
  userId: string;
  measuredAt?: string;
  weightKg: number;
  skeletalMuscleKg: number;
  bodyFatKg: number;
  heightCm?: number;
  bodyFatPct?: number;
  skeletalMusclePct?: number;
  visceralFatLevel?: number;
  bodyAge?: number;
  bmi?: number;
  restingMetabolismKcal?: number;
  note?: string;
}

/** Spring `Page<T>` shape returned by the list endpoints. */
export interface Page<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
  first: boolean;
  last: boolean;
  numberOfElements: number;
}

interface ListParams {
  userId: string;
  page?: number;
  size?: number;
  startDate?: string;
  endDate?: string;
}

const listBodyMeasurements = async ({
  userId,
  page = 0,
  size = 20,
  startDate,
  endDate,
}: ListParams): Promise<Page<BodyMeasurement>> => {
  let urlPath = `/admin/body-measurement/${userId}?page=${page}&size=${size}`;
  if (startDate) urlPath += `&startDate=${startDate}`;
  if (endDate) urlPath += `&endDate=${endDate}`;

  const result = await http.get({ path: urlPath, skipAuthRefresh: true });
  return result.data.data as Page<BodyMeasurement>;
};

/**
 * Latest scan for a member, or `null` when they have none. The list isn't
 * guaranteed to be sorted, so we resolve the max `measuredAt` on the client.
 */
const getLatestBodyMeasurement = async (
  userId: string
): Promise<BodyMeasurement | null> => {
  const pageData = await listBodyMeasurements({ userId, page: 0, size: 20 });
  const content = pageData?.content ?? [];
  if (content.length === 0) return null;
  return content.reduce((latest, current) =>
    new Date(current.measuredAt).getTime() >
    new Date(latest.measuredAt).getTime()
      ? current
      : latest
  );
};

const createBodyMeasurement = async (
  payload: CreateBodyMeasurementPayload
): Promise<BodyMeasurement> => {
  const result = await http.post({
    path: `/admin/body-measurement`,
    body: payload,
  });
  return result.data.data as BodyMeasurement;
};

const deleteBodyMeasurement = async (id: string): Promise<void> => {
  await http.deleteHttp({ path: `/admin/body-measurement/${id}`, body: {} });
};

export {
  listBodyMeasurements,
  getLatestBodyMeasurement,
  createBodyMeasurement,
  deleteBodyMeasurement,
};
